/**
 * 编译查询
 *
 * 预编译的查询执行计划，避免每次查询时重复构建匹配条件。
 * 提供便捷的迭代方法和变更检测支持。
 *
 * Compiled Query.
 * Pre-compiled query execution plan that avoids repeated condition building.
 * Provides convenient iteration methods and change detection support.
 *
 * @example
 * ```typescript
 * class MovementSystem extends EntitySystem {
 *     private _query: CompiledQuery<[typeof Position, typeof Velocity]>;
 *
 *     onInitialize(): void {
 *         this._query = this.scene.querySystem.compile(Position, Velocity);
 *     }
 *
 *     update(): void {
 *         this._query.forEach((entity, pos, vel) => {
 *             pos.x += vel.x * this.deltaTime;
 *             pos.y += vel.y * this.deltaTime;
 *         });
 *     }
 * }
 * ```
 */
/**
 * 编译查询类
 *
 * Compiled query class.
 */
export class CompiledQuery {
    /**
     * 创建编译查询
     *
     * Create compiled query.
     *
     * @param querySystem 查询系统引用 | Query system reference
     * @param componentTypes 组件类型列表 | Component type list
     */
    constructor(querySystem, ...componentTypes) {
        /**
         * 上次查询的版本号（用于缓存失效检测）
         *
         * Last query version (for cache invalidation detection).
         */
        this._lastVersion = -1;
        /**
         * 缓存的实体列表
         *
         * Cached entity list.
         */
        this._cachedEntities = [];
        this._querySystem = querySystem;
        this._componentTypes = componentTypes;
    }
    /**
     * 获取组件类型列表
     *
     * Get component type list.
     */
    get componentTypes() {
        return this._componentTypes;
    }
    /**
     * 获取匹配的实体列表
     *
     * Get matching entity list.
     */
    get entities() {
        this._refreshCache();
        return this._cachedEntities;
    }
    /**
     * 获取匹配的实体数量
     *
     * Get matching entity count.
     */
    get count() {
        return this.entities.length;
    }
    /**
     * 刷新缓存
     *
     * Refresh cache if needed.
     */
    _refreshCache() {
        const currentVersion = this._querySystem.version;
        if (this._lastVersion !== currentVersion) {
            const result = this._querySystem.queryAll(...this._componentTypes);
            this._cachedEntities = result.entities;
            this._lastVersion = currentVersion;
        }
    }
    /**
     * 遍历所有匹配的实体及其组件
     *
     * Iterate all matching entities with their components.
     *
     * @param callback 回调函数，接收实体和组件实例 | Callback receiving entity and component instances
     *
     * @example
     * ```typescript
     * query.forEach((entity, position, velocity) => {
     *     position.x += velocity.x * deltaTime;
     * });
     * ```
     */
    forEach(callback) {
        const entities = this.entities;
        const componentTypes = this._componentTypes;
        const typeCount = componentTypes.length;
        for (let i = 0, len = entities.length; i < len; i++) {
            const entity = entities[i];
            const components = new Array(typeCount);
            // 获取所有组件
            for (let j = 0; j < typeCount; j++) {
                const component = entity.getComponent(componentTypes[j]);
                if (!component) {
                    // 组件不存在，跳过这个实体
                    continue;
                }
                components[j] = component;
            }
            // 调用回调
            callback(entity, ...components);
        }
    }
    /**
     * 遍历自指定 epoch 以来发生变更的实体
     *
     * Iterate entities with components changed since specified epoch.
     *
     * @param sinceEpoch 检查点 epoch | Checkpoint epoch
     * @param callback 回调函数 | Callback function
     *
     * @example
     * ```typescript
     * class PhysicsSystem extends EntitySystem {
     *     private _lastEpoch = 0;
     *
     *     update(): void {
     *         this._query.forEachChanged(this._lastEpoch, (entity, pos, vel) => {
     *             // 只处理变更的实体
     *         });
     *         this._lastEpoch = this.scene.epochManager.current;
     *     }
     * }
     * ```
     */
    forEachChanged(sinceEpoch, callback) {
        const entities = this.entities;
        const componentTypes = this._componentTypes;
        const typeCount = componentTypes.length;
        for (let i = 0, len = entities.length; i < len; i++) {
            const entity = entities[i];
            const components = new Array(typeCount);
            let hasChanged = false;
            // 获取所有组件并检查变更
            for (let j = 0; j < typeCount; j++) {
                const component = entity.getComponent(componentTypes[j]);
                if (!component) {
                    continue;
                }
                components[j] = component;
                if (component.lastWriteEpoch > sinceEpoch) {
                    hasChanged = true;
                }
            }
            // 只在有变更时调用回调
            if (hasChanged) {
                callback(entity, ...components);
            }
        }
    }
    /**
     * 获取第一个匹配的实体及其组件
     *
     * Get first matching entity with its components.
     *
     * @returns 实体和组件元组，或 null | Entity and components tuple, or null
     */
    first() {
        const entities = this.entities;
        if (entities.length === 0) {
            return null;
        }
        const entity = entities[0];
        const components = [];
        for (const type of this._componentTypes) {
            const component = entity.getComponent(type);
            if (!component) {
                return null;
            }
            components.push(component);
        }
        return [entity, ...components];
    }
    /**
     * 转换为数组
     *
     * Convert to array.
     *
     * @returns 实体和组件元组数组 | Array of entity and components tuples
     */
    toArray() {
        const result = [];
        this.forEach((entity, ...components) => {
            result.push([entity, ...components]);
        });
        return result;
    }
    /**
     * 映射转换
     *
     * Map transformation.
     *
     * @param callback 转换函数 | Transform function
     * @returns 转换结果数组 | Array of transform results
     */
    map(callback) {
        const result = [];
        this.forEach((entity, ...components) => {
            result.push(callback(entity, ...components));
        });
        return result;
    }
    /**
     * 过滤实体
     *
     * Filter entities.
     *
     * @param predicate 过滤条件 | Filter predicate
     * @returns 过滤后的实体数组 | Filtered entity array
     */
    filter(predicate) {
        const result = [];
        this.forEach((entity, ...components) => {
            if (predicate(entity, ...components)) {
                result.push(entity);
            }
        });
        return result;
    }
    /**
     * 查找满足条件的实体
     *
     * Find entity matching predicate.
     *
     * @param predicate 查找条件 | Find predicate
     * @returns 找到的实体或 undefined | Found entity or undefined
     */
    find(predicate) {
        const entities = this.entities;
        const componentTypes = this._componentTypes;
        const typeCount = componentTypes.length;
        for (let i = 0, len = entities.length; i < len; i++) {
            const entity = entities[i];
            const components = new Array(typeCount);
            for (let j = 0; j < typeCount; j++) {
                const component = entity.getComponent(componentTypes[j]);
                if (!component) {
                    continue;
                }
                components[j] = component;
            }
            if (predicate(entity, ...components)) {
                return entity;
            }
        }
        return undefined;
    }
    /**
     * 检查是否有任何实体匹配
     *
     * Check if any entity matches.
     */
    any() {
        return this.count > 0;
    }
    /**
     * 检查是否没有实体匹配
     *
     * Check if no entity matches.
     */
    empty() {
        return this.count === 0;
    }
}
//# sourceMappingURL=CompiledQuery.js.map